/** @jsx jsx */
import {
  Box,
  Button,
  Flex,
  FormAssistive,
  FormGroup,
  FormLabel,
  Grid,
  Heading,
  InfoBox,
  InformationIcon,
  Paragraph,
} from "@eworkit/iris";
import { startOfDay } from "date-fns";
import { FormikProps } from "formik";
import React, { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";
import * as Yup from "yup";

import { useAnalytics } from "@/components/analytics/useAnalytics";
import { FormikTextArea, VALIDATION_TYPES } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { FormikNumberInput } from "@/components/Formik/FormikNumberInput";
import { If } from "@/components/If";
import { UserContext } from "@/components/User";
import { TDate } from "@/core/dates";
import { ICodelist } from "@/data/codelists";
import { ConsultantProfileContactPerson } from "@/data/consultant-profiles/types/ConsultantProfileContactPerson";
import { IJobApplicationRate, MOTIVATION_MAX_LENGTH, PHONE_NUMBER_MAX_LENGTH } from "@/data/job-applications";
import { JobApplication } from "@/data/job-applications/types/JobApplication";
import { JobApplicationCostsDto } from "@/data/job-applications/types/JobApplicationCostsDto";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { formatMultipleLocations } from "@/data/locations";
import { fetchCostCalculation } from "@/data/payexpress/service";
import { isErrorVisible } from "@/formikComponents";
import { useFormats } from "@/hooks/Formats";
import { countDecimals } from "@/utils/numbers/countDecimals";
import { AvailabilityForm } from "@/views/consultant-profiles/details/availability/AvailabilityForm";
import { useApplyWizard } from "@/views/job-applications/apply/_components/ApplyWizardContext";
import { ApplyApplicationContact } from "@/views/job-applications/apply/_details/ApplyApplicationContact";
import { ApplyPayexpress } from "@/views/job-applications/apply/_details/ApplyPayexpress";
import { ApplyTotalPayout } from "@/views/job-applications/apply/_details/ApplyTotalPayout";

import { useApplyWizardDraftUpdate } from "../_components/useApplyWizardDraftUpdate";

export interface DetailsFormValues {
  rate: IJobApplicationRate;
  motivation: string;
  contactPerson: ConsultantProfileContactPerson;
  availability: ICodelist["code"];
  availabilityDate: TDate | null;
  // payExpress?: PayexpressJobRequestApplicationDto;
  payExpressSelected: boolean;
  costs: JobApplicationCostsDto;
}

export const detailsValidation = Yup.object().shape({
  availability: Yup.string().nullable(true).required(VALIDATION_TYPES.REQUIRED),
  availabilityDate: Yup.date()
    .min(startOfDay(new Date()), VALIDATION_TYPES.FUTURE_DATE_REQUIRED)
    .nullable(true)
    .when("availability", {
      is: v => v === "SPECIFIC_DATE",
      then: Yup.date().required(VALIDATION_TYPES.REQUIRED),
    }),
  motivation: Yup.string()
    .trim()
    .required(VALIDATION_TYPES.REQUIRED)
    .max(MOTIVATION_MAX_LENGTH, VALIDATION_TYPES.MAX_LENGTH),
  rate: Yup.object().shape({
    ratePerHour: Yup.number()
      .transform((value, originalValue) => (/\s/.test(originalValue) ? 0 : value))
      .nullable()
      .typeError(VALIDATION_TYPES.NUMBERS)
      .required(VALIDATION_TYPES.MORE_THAN)
      .test({
        name: "two-decimal-places",
        test: (v: number) => countDecimals(v) <= 2,
        message: VALIDATION_TYPES.MAX_2_DECIMAL_PLACES,
      })
      .max(999999999999999, VALIDATION_TYPES.TOO_BIG_NUMBER)
      .moreThan(0, VALIDATION_TYPES.MORE_THAN),
  }),
  contactPerson: Yup.object().shape({
    phone: Yup.object()
      .nullable(true)
      .required(VALIDATION_TYPES.REQUIRED)
      .shape({
        phoneNumber: Yup.string()
          .required(VALIDATION_TYPES.REQUIRED)
          .max(PHONE_NUMBER_MAX_LENGTH, VALIDATION_TYPES.MAX_LENGTH)
          .test({
            message: VALIDATION_TYPES.NUMBERS,
            test: (phone: string) => /^\d+$/.test(phone),
          }),
        phoneCode: Yup.string().nullable(true).required(VALIDATION_TYPES.REQUIRED),
      }),
  }),
});

export const transformDraftWithDetails = (values: DetailsFormValues) => (draft: JobApplication) => ({
  ...draft,
  consultantProfile: {
    ...draft.consultantProfile,
    availability: values.availability,
    availabilityDate: values.availabilityDate,
    contactPerson: values.contactPerson,
  },
  motivation: values.motivation,
  rate: {
    ratePerHour: values.rate.ratePerHour || 0,
    currency: values.rate.currency,
  },
  payExpressSelected: values.payExpressSelected,
  costs: values.costs,
});

interface PayExpressConfig {
  available: boolean;
  feePercentage: number | null;
  isPayExpress: boolean;
}

interface ApplyDetailsFormProps {
  formik: FormikProps<DetailsFormValues>;
  jobRequest: JobRequest;
}

export const ApplyDetailsForm: FC<ApplyDetailsFormProps> = ({ formik, jobRequest }) => {
  const { t } = useTranslation("session");
  const { formatRate } = useFormats();
  const { sendEvent } = useAnalytics();

  const { profile, draft } = useApplyWizard();
  useApplyWizardDraftUpdate(transformDraftWithDetails(formik.values));

  const User = useContext(UserContext);

  const rateChangeTimeout = useRef<NodeJS.Timeout>();
  const calculatedPayExpressFeeAmount = useRef<number>();

  const role = profile?.id === User.contextConsultantProfileId ? "CONSULTANT" : "MANAGER";

  const motivationCharsLeft = MOTIVATION_MAX_LENGTH - formik.values.motivation.length;

  const [payExpressConfig, setPayExpressConfig] = useState<PayExpressConfig>({
    available: false,
    isPayExpress: draft?.payExpressSelected ?? jobRequest.payExpressConfig?.defaultPayExpress ?? false,
    feePercentage: draft?.jobRequest?.payExpressConfig?.payExpressFeePercentage ?? null,
  });

  const supplierSpecifiedPaymentTerms =
    (draft?.jobRequest?.supplierPaymentTerm ?? jobRequest.supplierPaymentTerm ?? 0) > 0;
  const useServiceFeeCalculation = Boolean(jobRequest.brokerFee?.percentage);
  const usePayExpressFeeCalculation = payExpressConfig.available && User.contextCompany;

  const useCalculations = useServiceFeeCalculation || usePayExpressFeeCalculation;

  const displayCalculations =
    useServiceFeeCalculation || (usePayExpressFeeCalculation && supplierSpecifiedPaymentTerms);

  const handleCalculate = useCallback(
    (direction, rate) => {
      const result = fetchCostCalculation({
        data: {
          direction: direction,
          payExpressFeePercentage: payExpressConfig.isPayExpress ? payExpressConfig.feePercentage || 0 : 0,
          rate: Number(rate) || 0,
          serviceFeePercentage: jobRequest.brokerFee?.percentage || 0,
        },
      });
      return result;
    },
    [payExpressConfig, jobRequest, formik],
  );

  const handleRateChange = useCallback(
    (direction = "RATE_TO_TOTAL_PAYOUT", initialRate = formik.values.rate.ratePerHour) => {
      let rate = initialRate;
      if (useCalculations) {
        if (rateChangeTimeout.current) {
          clearTimeout(rateChangeTimeout.current);
          rateChangeTimeout.current = undefined;
        }

        rateChangeTimeout.current = setTimeout(() => {
          if (rate) {
            if (countDecimals(rate) > 2) {
              rate = Number(rate).toFixed(2);
              formik.setFieldValue(
                direction === "RATE_TO_TOTAL_PAYOUT" ? "rate.ratePerHour" : "costs.finalRatePerHour",
                rate,
                true,
              );
            }
            if (rate > 9999999999 || isNaN(rate) || rate <= 0) {
              return;
            }

            handleCalculate(direction, rate).then(result => {
              calculatedPayExpressFeeAmount.current = result.payExpressFeeAmount;
              formik.setFieldValue("costs.brokerFeeAmount", result.serviceFeeAmount);
              formik.setFieldValue("costs.brokerFeePercentage", jobRequest.brokerFee?.percentage ?? 0, true);
              formik.setFieldValue(
                "costs.payExpressFeeAmount",
                payExpressConfig.isPayExpress ? result.payExpressFeeAmount : 0,
              );
              formik.setFieldValue(
                "costs.payExpressFeePercentage",
                payExpressConfig.isPayExpress ? jobRequest.payExpressConfig?.payExpressFeePercentage : 0,
              );
              if (direction === "RATE_TO_TOTAL_PAYOUT") {
                formik.setFieldValue("costs.finalRatePerHour", result.finalRatePerHour, true);
              } else {
                formik.setFieldValue("rate.ratePerHour", result.initialRate, true);
              }
            });
          } else {
            calculatedPayExpressFeeAmount.current = undefined;
            formik.setFieldValue("rate.ratePerHour", null, true);
            formik.setFieldValue("costs.brokerFeeAmount", null, true);
            formik.setFieldValue("costs.brokerFeePercentage", null, true);
            formik.setFieldValue("costs.finalRatePerHour", null, true);
          }
        }, 300);
      }
    },
    [
      formik,
      handleCalculate,
      payExpressConfig,
      useCalculations,
      rateChangeTimeout.current,
      calculatedPayExpressFeeAmount.current,
    ],
  );

  const handlePayExpressUnavailable = useCallback(() => {
    if (jobRequest.brokerFee?.percentage) {
      formik.setFieldValue("costs.payExpressFeeAmount", null);
    } else {
      formik.setFieldValue("costs", undefined, true);
    }
    setPayExpressConfig({
      available: false,
      isPayExpress: false,
      feePercentage: null,
    });
  }, [formik, jobRequest]);

  const handlePayExpressChange = useCallback(
    (isPayExpress, fee, term) => {
      setPayExpressConfig({
        available: true,
        isPayExpress: isPayExpress,
        feePercentage: fee,
      });

      formik.setFieldValue("payExpressSelected", isPayExpress);

      sendEvent({
        type: "apply_job_payment",
        id: jobRequest.id,
        name: jobRequest.title!,
        location: formatMultipleLocations(t, jobRequest.locations, jobRequest.locations?.length),
        payment: `${term} days`,
      });
    },
    [setPayExpressConfig, formik, jobRequest, sendEvent, t],
  );

  useEffect(() => {
    if (payExpressConfig.available || jobRequest.brokerFee?.percentage) {
      handleRateChange();
    }
  }, [payExpressConfig]);

  useEffect(() => {
    if (!User.contextCompany && !jobRequest.brokerFee?.percentage) {
      formik.setFieldValue("costs", undefined, true);
    }
  }, [User]);

  useEffect(() => {
    return () => {
      if (rateChangeTimeout.current) {
        clearTimeout(rateChangeTimeout.current);
        rateChangeTimeout.current = undefined;
      }
    };
  }, []);

  return (
    <React.Fragment>
      <Flex flexDirection="column" gap="xl">
        <Flex flexDirection="column" gap="sm">
          <Heading size="h-md">{t("JOB_REQUESTS.APPLY.WIZARD.DETAILS")}</Heading>

          <AvailabilityForm
            availability={formik.values.availability}
            availabilityDate={formik.values.availabilityDate}
            setAvailabilityDateField={(date: string | null) => {
              formik.setFieldValue("availabilityDate", date, true);
            }}
          />

          <FormGroup
            label={
              <FormLabel
                label={t(`JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.MOTIVATION_LABEL_${role}`)}
                required
              />
            }
            error={
              <FormikError
                name="motivation"
                overrideMessages={{
                  MAX_LENGTH: t("JOB_APPLICATIONS.PREVIEW.APPLICATION_DETAILS.MOTIVATION_MAX_LENGTH"),
                }}
              />
            }
            assistive={
              motivationCharsLeft >= 0 ? (
                <FormAssistive
                  icon={<InformationIcon color="n700" />}
                  text={t("JOB_REQUESTS.APPLY.WIZARD.CHARACTERS_REMAINING", {
                    chars: motivationCharsLeft,
                  })}
                />
              ) : undefined
            }>
            <FormikTextArea name="motivation" rows={6} sx={{ minHeight: 90 }} />
          </FormGroup>
        </Flex>

        <Flex flexDirection="column" gap="sm">
          <Flex flexDirection="column">
            <Heading size="h-md">{t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.PAYMENT")}</Heading>
            {displayCalculations && (
              <Paragraph size="p-md" color="n700">
                {jobRequest.rate.clientRateType === "HOURLY"
                  ? t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.PAYMENT_INFO")
                  : t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.PAYMENT_INFO_FIXED")}
              </Paragraph>
            )}
          </Flex>

          <Grid columns={["1fr", null, null, "1fr 1fr 1fr"]}>
            <Flex flexDirection="column">
              <FormGroup
                label={
                  <FormLabel
                    label={t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.RATE_PLACEHOLDER")}
                    required
                  />
                }
                error={
                  <FormikError
                    name="rate.ratePerHour"
                    overrideMessages={{
                      [VALIDATION_TYPES.MORE_THAN]: t("common:VALIDATION_MESSAGES.MORE_THAN", {
                        value: 0,
                      }),
                      [VALIDATION_TYPES.TOO_BIG_NUMBER]: t(
                        "JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.RATE_TOO_BIG",
                      ),
                      [VALIDATION_TYPES.MAX_LENGTH]: t(
                        "JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.RATE_TOO_BIG",
                      ),
                    }}
                  />
                }>
                <FormikNumberInput
                  name="rate.ratePerHour"
                  onChange={value => {
                    handleRateChange("RATE_TO_TOTAL_PAYOUT", value);
                  }}
                  precision={2}
                  rightPlaceholder={
                    <Paragraph size="p-md" color="n700">
                      {formik.values.rate.currency}
                      {jobRequest.rate.clientRateType === "FIXED" ? "" : "/h"}
                    </Paragraph>
                  }
                  size="xl"
                />
              </FormGroup>
            </Flex>
          </Grid>

          {Boolean(jobRequest.rate.maxRate) && formik.values.rate.ratePerHour > jobRequest.rate.maxRate! && (
            <InfoBox
              text={t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.HOURLY_RATE_EXCEEDS")}
              variant="warning"
            />
          )}

          {User.contextCompany && jobRequest.administratorLegalEntityClient?.id && jobRequest.legalEntityClient?.id && (
            <FormGroup
              error={
                <FormikError
                  name="payExpressSelected"
                  overrideMessages={{
                    REQUIRED: t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.ERROR"),
                  }}
                />
              }>
              <ApplyPayexpress
                jobRequest={jobRequest}
                payExpressChosen={payExpressConfig.isPayExpress}
                feeAmount={
                  calculatedPayExpressFeeAmount.current
                    ? formatRate(calculatedPayExpressFeeAmount.current, formik.values.rate.currency!, "h", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : undefined
                }
                onChange={(isPayExpress, fee, term) => {
                  handlePayExpressChange(isPayExpress, fee, term);
                }}
                onPayExpressUnavailable={handlePayExpressUnavailable}
                requiredError={!!isErrorVisible(formik, "payExpressSelected")}
              />
            </FormGroup>
          )}

          {Boolean(jobRequest.brokerFee?.percentage) && (
            <Flex gap="xxs" flexDirection="column">
              <FormLabel label={t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.FEES.TITLE")} />
              <InfoBox
                sx={{
                  backgroundColor: "n0",
                  border: "primary",
                }}
                component={
                  <Flex flexDirection="column" gap="xs">
                    <Flex justifyContent="space-between">
                      <Paragraph bold>{t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.FEES.EWORK_SERVICES")}</Paragraph>
                      {formik.values.costs?.brokerFeeAmount && jobRequest.rate.clientRateType !== "FIXED"
                        ? formatRate(formik.values.costs?.brokerFeeAmount, formik.values.rate.currency!, "h", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : null}
                    </Flex>
                    <If predicate={Boolean(jobRequest.brokerFee)}>
                      <Paragraph>{jobRequest.brokerFee?.description}</Paragraph>
                    </If>
                  </Flex>
                }
              />
            </Flex>
          )}

          {displayCalculations && jobRequest.rate.clientRateType !== "FIXED" && (
            <ApplyTotalPayout
              rate={formik.values.rate}
              onChange={value => {
                handleRateChange("TOTAL_PAYOUT_TO_RATE", value);
              }}
            />
          )}
        </Flex>

        <Flex flexDirection="column" gap="sm">
          <Heading size="h-md">
            {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.APPLICATION_CONTACT")}
          </Heading>

          <ApplyApplicationContact formik={formik} />
        </Flex>

        <Box>
          <Button onClick={formik.submitForm} disabled={formik.isSubmitting} size="lg">
            {t("JOB_REQUESTS.APPLY.WIZARD.CONTINUE")}
          </Button>
        </Box>
      </Flex>
    </React.Fragment>
  );
};
